import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLessonsStore } from "@/stores/lessons";
import { Close, CloudUpload, Image, VideoFile } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  Alert,
  Fade,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { INewLesson } from "../../../../@types";
import { Modal } from "../../../../components";
import { useInputText } from "../../../../hooks";
import services from "../../../../services";
import { useData as useModulesData } from "../../modules/use-data";
import { ModalUpdateProps } from "../@types";
import { useData } from "../use-data";
import { IconPreview } from "./preview";

export default function ModalUpdate({ open, onClose }: ModalUpdateProps) {
  const { modules } = useModulesData();
  const { refetch } = useData();
  const { selectedItem } = useLessonsStore();
  const initialValues = { title: "", description: "" };
  const input = useInputText(initialValues);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [video, setVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [moduleId, setModuleId] = useState<string | null>(null);
  const [existingVideo, setExistingVideo] = useState<{
    title?: string;
    thumbnail: string;
  } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Estado para o campo opcional position
  const [position, setPosition] = useState<number | "" | undefined>("");
  // Estado para mostrar/ocultar secção avançado
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [loading, setLoading] = useState<{
    loading: boolean;
    status?: string;
    progress?: number;
  }>({ loading: false });
  const [error, setError] = useState<string | null>(null);

  // Steps para o loader
  const UPDATE_STEPS = [
    { key: "update", label: "Atualizar dados" },
    { key: "thumbnail", label: "Carregar miniatura" },
    { key: "video", label: "Carregar vídeo" },
    { key: "finish", label: "Finalizar" },
  ];
  const [stepStatus, setStepStatus] = useState<{
    [key: string]: "pending" | "in_progress" | "done" | "error";
  }>({});

  const setStep = (
    stepKey: string,
    status: "pending" | "in_progress" | "done" | "error"
  ) => {
    setStepStatus((prev) => ({ ...prev, [stepKey]: status }));
  };

  const onModuleIdChange = (event: SelectChangeEvent) =>
    setModuleId(event.target.value);

  const onThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: newFiles } = event.currentTarget;
    if (newFiles && newFiles[0]) {
      const file = newFiles[0];
      if (file.type.startsWith("image/")) {
        setThumbnail(file);
      } else {
        toast.error(
          "Por favor, selecione um arquivo de imagem válido (JPG, PNG)"
        );
        if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
      }
    } else {
      setThumbnail(null);
    }
  };

  const onVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: newFiles } = event.currentTarget;
    if (newFiles && newFiles[0]) {
      const videoFile = newFiles[0];
      if (videoFile.type.startsWith("video/")) {
        setVideo(videoFile);
        const previewUrl = URL.createObjectURL(videoFile);
        setVideoPreview(previewUrl);
      } else {
        toast.error("Por favor, selecione um arquivo de vídeo válido");
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } else {
      setVideo(null);
      setVideoPreview(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find((file) => file.type.startsWith("video/"));
    if (videoFile) {
      setVideo(videoFile);
      const previewUrl = URL.createObjectURL(videoFile);
      setVideoPreview(previewUrl);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } else {
      toast.error("Por favor, arraste um arquivo de vídeo válido");
    }
  };

  const removeVideo = () => {
    setVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
  };

  const resetState = useCallback(() => {
    input.clear();
    setThumbnail(null);
    setVideo(null);
    setVideoPreview(null);
    setExistingVideo(null);
    setModuleId(modules ? modules[0]?.id : null);
    setError(null);
    setLoading({ loading: false });
    setIsDragOver(false);
    setPosition(""); // Limpar position
    setShowAdvanced(false); // Ocultar secção avançado
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
  }, [modules]);

  const closeModal = () => {
    resetState();
    refetch();
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      if (error) setError(null);
      if (!selectedItem)
        throw new Error("Nenhuma aula selecionada para atualização");
      setLoading({
        loading: true,
        status: "A preparar para atualizar a aula...",
        progress: 0,
      });
      setStep("update", "in_progress");
      if (thumbnail && thumbnail.size > 20 * 1024 * 1024)
        throw new Error("O tamanho da miniatura não pode ser maior que 20MB");
      const { title, description } = input;
      const data: INewLesson = {
        title,
        description,
        moduleId: moduleId!,
        ...(showAdvanced && position !== undefined && position !== ""
          ? { position: Number(position) }
          : {}),
      };
      if (thumbnail) {
        data.thumbnail = {
          contentType: thumbnail.type,
          filename: thumbnail.name,
          size: thumbnail.size,
        };
      }
      if (video) {
        data.video = {
          contentType: video.type,
          filename: video.name,
          size: video.size,
        };
      }
      setLoading({
        loading: true,
        status: "A atualizar dados da aula...",
        progress: 10,
      });
      const updatedLesson = await services.lessons.updateById(
        selectedItem.id,
        data
      );
      setStep("update", "done");
      if (updatedLesson) {
        if (updatedLesson.thumbnailUploadURL && thumbnail) {
          setStep("thumbnail", "in_progress");
          try {
            setLoading({
              loading: true,
              status: "A carregar miniatura da aula...",
              progress: 20,
            });
            await axios.put(updatedLesson.thumbnailUploadURL, thumbnail, {
              headers: { "Content-Type": thumbnail.type },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / (progressEvent.total || 1)
                );
                const adjustedProgress =
                  20 + Math.floor(percentCompleted * 0.3);
                setLoading((prev) => ({
                  ...prev,
                  status: `A carregar miniatura: ${percentCompleted}%`,
                  progress: adjustedProgress,
                }));
              },
            });
            setLoading({
              loading: true,
              status: "Miniatura carregada com sucesso",
              progress: 50,
            });
            setStep("thumbnail", "done");
            toast.success("Miniatura atualizada com sucesso");
          } catch (error) {
            setStep("thumbnail", "error");
            throw new Error(
              "Falha ao atualizar a miniatura.\n" + (error as Error).message
            );
          }
        } else {
          setStep("thumbnail", "done");
          setLoading({
            loading: true,
            status: "A preparar o carregamento do vídeo...",
            progress: 50,
          });
        }
        if (updatedLesson.videoUploadURL && video) {
          setStep("video", "in_progress");
          try {
            setLoading({
              loading: true,
              status: "A iniciar carregamento do vídeo...",
              progress: 50,
            });

            // --- NOVA ABORDAGEM: Uppy + Tus (igual ao modal-register) ---
            const Uppy = (await import("@uppy/core")).default;
            const Tus = (await import("@uppy/tus")).default;
            const uploadUppy = new Uppy({
              autoProceed: false,
              restrictions: { maxNumberOfFiles: 1 },
              debug: true,
            });

            // Função para calcular chunkSize
            const calculateChunkSize = (fileSize: number) => {
              if (fileSize < 100 * 1024 * 1024) return 1 * 1024 * 1024;
              if (fileSize < 500 * 1024 * 1024) return 5 * 1024 * 1024;
              return 10 * 1024 * 1024;
            };

            uploadUppy.use(Tus, {
              endpoint: updatedLesson.videoUploadURL,
              chunkSize: calculateChunkSize(video.size),
              retryDelays: [0, 1000, 3000, 5000],
            });

            await uploadUppy.addFile({
              name: video.name,
              type: video.type,
              data: video,
            });

            uploadUppy.on("upload-progress", (_, progress) => {
              const percentCompleted = Math.round(
                (progress.bytesUploaded * 100) / (progress.bytesTotal || 1)
              );
              const adjustedProgress = 50 + Math.floor(percentCompleted * 0.45);
              setLoading((prev) => ({
                ...prev,
                status: `A carregar vídeo: ${percentCompleted}%`,
                progress: adjustedProgress,
              }));
            });

            uploadUppy.on("upload-success", (_, __) => {
              setLoading({
                loading: true,
                status: "Vídeo carregado com sucesso!",
                progress: 95,
              });
              toast.success("Vídeo atualizado com sucesso");
            });

            uploadUppy.on("upload-error", (_, error, __) => {
              setStep("video", "error");
              throw new Error(`Falha no upload do vídeo: ${error.message}`);
            });

            const uploadResult = await uploadUppy.upload();

            if (
              uploadResult &&
              uploadResult.failed &&
              uploadResult.failed.length > 0
            ) {
              setStep("video", "error");
              throw new Error("Falha no upload do vídeo");
            }

            setLoading({
              loading: true,
              status: "Vídeo carregado com sucesso! A finalizar...",
              progress: 95,
            });
            setStep("video", "done");
          } catch (error) {
            setStep("video", "error");
            console.error("Upload error:", error);
            throw new Error(
              "Falha ao atualizar o vídeo.\n" +
                (error instanceof Error ? error.message : error)
            );
          }
        } else {
          setStep("video", "done");
        }
        setLoading({
          loading: true,
          status: "Aula atualizada com sucesso!",
          progress: 100,
        });
        setStep("finish", "in_progress");
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
      setStep("finish", "done");
      toast.success("Aula atualizada com sucesso.");
      closeModal();
    } catch (error) {
      setError((error as Error).message);
      // Marcar o step atual como erro
      const currentStep = Object.keys(stepStatus).find(
        (k) => stepStatus[k] === "in_progress"
      );
      if (currentStep) setStep(currentStep, "error");
    } finally {
      setLoading({ loading: false, status: undefined, progress: 0 });
    }
  };

  useEffect(() => {
    if (open && selectedItem) {
      input.updateInitial("title", selectedItem.title || "");
      input.updateInitial("description", selectedItem.description || "");
      if (selectedItem.moduleId) setModuleId(selectedItem.moduleId);
      if (typeof selectedItem.position === "number") {
        setPosition(selectedItem.position);
        setShowAdvanced(true);
      } else {
        setPosition("");
        setShowAdvanced(false);
      }
      if (selectedItem.video) {
        setExistingVideo({
          title: selectedItem.video.title,
          thumbnail: selectedItem.video.thumbnail || "",
        });
        // Preencher o preview do vídeo antigo
        setVideoPreview(selectedItem.video.url || null);
      } else {
        setVideoPreview(null);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
    }
  }, [open, selectedItem]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="w-full max-w-[90%] md:max-w-[80%] lg:max-w-[1020px] max-h-[95%]"
      head={<h2 className="text-xl font-bold">Atualizar Lição</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-8">
          {loading.loading ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-6">
              {/* Steps UI */}
              <div className="w-full  max-w-md text-center mb-6">
                <ol className="grid grid-cols-2 gap-3">
                  {UPDATE_STEPS.map((step, idx) => {
                    const status =
                      stepStatus[step.key] ||
                      (idx === 0 ? "in_progress" : "pending");
                    let icon = <HourglassEmptyIcon className="text-gray-400" />;
                    if (status === "done")
                      icon = <CheckCircleIcon className="text-green-600" />;
                    if (status === "error")
                      icon = <ErrorIcon className="text-red-600" />;
                    if (status === "in_progress")
                      icon = <Loader2 className="size-5 animate-spin" />;
                    return (
                      <li key={step.key} className="flex items-center gap-2">
                        {icon}
                        <span
                          className={`text-base ${status === "done" ? "text-green-700" : status === "error" ? "text-red-700" : status === "in_progress" ? "text-primary" : "text-gray-500"}`}
                        >
                          {step.label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
              <div className="w-full max-w-md text-center">
                <h3 className="text-lg font-medium mb-4">
                  {loading.status || "A processar..."}
                </h3>
                <LinearProgress
                  variant="determinate"
                  value={loading.progress || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <p className="mt-2 text-sm">
                  {loading.progress ? `${loading.progress}%` : ""}
                </p>
              </div>
              {error && (
                <Alert
                  severity="error"
                  className="w-full max-w-md"
                  sx={{ mt: 2 }}
                >
                  {error}
                </Alert>
              )}
              {/* Botão Cancelar só durante a atualização dos dados */}
              {stepStatus.update === "in_progress" && (
                <div className="w-full max-w-[250px] flex justify-center  mt-4">
                  <Button
                    variant="outline"
                    onClick={closeModal}
                    className="w-full py-3 h-fit"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-6 h-full w-full">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-full w-full">
                {/* Coluna da esquerda - Upload e Preview */}
                <div className="space-y-6 lg:col-span-2">
                  <ScrollArea className="flex-1 h-[500px] w-full">
                    {/* Upload do Vídeo */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Vídeo da Lição</h3>
                      {!video && videoPreview ? (
                        <div className="space-y-4">
                          <div className="border rounded-lg px-4 pt-2 pb-0 ">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <VideoFile className="h-6 w-6 text-blue-500" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {existingVideo?.title || "Vídeo existente"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="border rounded-lg overflow-hidden bg-black">
                            <video
                              src={videoPreview}
                              className="w-full aspect-video object-contain"
                              controls
                            />
                          </div>
                        </div>
                      ) : !video ? (
                        <div
                          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${isDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <CloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <div className="space-y-2">
                            <p className="text-lg font-medium">
                              Arraste e largue o vídeo aqui
                            </p>
                            <p className="text-sm text-gray-500">
                              ou clique para selecionar um arquivo
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="border rounded-lg px-4 pt-2 pb-0 ">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <VideoFile className="h-6 w-6 text-blue-500" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {video.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(video.size)}
                                  </p>
                                </div>
                              </div>
                              <IconButton onClick={removeVideo} size="small">
                                <Close />
                              </IconButton>
                            </div>
                          </div>
                          {videoPreview && (
                            <div className="border rounded-lg overflow-hidden bg-black">
                              <video
                                src={videoPreview}
                                className="w-full aspect-video object-contain"
                                controls
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        onChange={onVideoChange}
                        className="hidden"
                      />
                    </div>
                    {/* Miniatura */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Miniatura da Lição
                      </h3>
                      {!thumbnail ? (
                        <div
                          className="border-2 border-dashed rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                          onClick={() => thumbnailInputRef.current?.click()}
                        >
                          <Image className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            Clique para adicionar uma miniatura
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            JPG, PNG até 20MB
                          </p>
                        </div>
                      ) : (
                        <div className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Image className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-medium">
                                Miniatura selecionada
                              </span>
                            </div>
                            <IconButton onClick={removeThumbnail} size="small">
                              <Close />
                            </IconButton>
                          </div>
                          <div className="relative aspect-video  rounded overflow-hidden">
                            <IconPreview
                              alt="pré-visualização da miniatura da lição"
                              file={thumbnail}
                              preview={existingVideo?.thumbnail}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {thumbnail.name} (
                            {thumbnail.size
                              ? formatFileSize(thumbnail.size)
                              : ""}
                            )
                          </p>
                        </div>
                      )}
                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept=".jpg, .png"
                        onChange={onThumbnailChange}
                        className="hidden"
                      />
                    </div>
                  </ScrollArea>
                </div>
                {/* Coluna da direita - Informações */}
                <div className="lg:col-span-3 flex flex-col h-full">
                  <ScrollArea className="flex-1 h-[600px]">
                    <div className="space-y-6 pr-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">
                          Informações da Lição
                        </h3>
                        <div className="space-y-3">
                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                              <span>Vídeo:</span>
                              <span
                                className={
                                  video ? "text-green-600" : "text-gray-400"
                                }
                              >
                                {video ? "✓ Selecionado" : "Não selecionado"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Miniatura:</span>
                              <span
                                className={
                                  thumbnail ? "text-green-600" : "text-gray-400"
                                }
                              >
                                {thumbnail ? "✓ Selecionada" : "Opcional"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Título:</span>
                              <span
                                className={
                                  input.title
                                    ? "text-green-600"
                                    : "text-gray-400"
                                }
                              >
                                {input.title ? "✓ Preenchido" : "Obrigatório"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Descrição:</span>
                              <span
                                className={
                                  input.description
                                    ? "text-green-600"
                                    : "text-gray-400"
                                }
                              >
                                {input.description
                                  ? "✓ Preenchida"
                                  : "Obrigatória"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <TextField
                          required
                          label="Título da Lição"
                          name="title"
                          value={input.title}
                          onChange={input.onChange}
                          onBlur={input.onBlur}
                          error={!!input.errors.title}
                          helperText={input.errors.title}
                          disabled={loading.loading}
                          fullWidth
                          size="small"
                        />
                        {moduleId && modules && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Módulo
                            </label>
                            <Select
                              value={moduleId}
                              onChange={onModuleIdChange}
                              disabled={loading.loading}
                              fullWidth
                              size="small"
                            >
                              {modules.map(({ id, name }) => (
                                <MenuItem
                                  disabled={loading.loading}
                                  key={id}
                                  value={id}
                                >
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        )}
                        <TextField
                          required
                          minRows={4}
                          maxRows={8}
                          multiline
                          label="Descrição"
                          name="description"
                          value={input.description}
                          onChange={input.onChange}
                          onBlur={input.onBlur}
                          error={!!input.errors.description}
                          helperText={input.errors.description}
                          disabled={loading.loading}
                          fullWidth
                          size="small"
                        />
                        {/* Checkbox para mostrar/ocultar secção avançado */}
                        <div className="pt-4 pl-3 mt-4 border-t">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showAdvanced}
                                onCheckedChange={(checked) => {
                                  setShowAdvanced(!!checked);
                                }}
                                disabled={loading.loading}
                                className="border-text mr-2 "
                              />
                            }
                            label="Mostrar opções avançadas"
                          />
                          {showAdvanced && (
                            <div className="mt-2">
                              <h4 className="text-base font-semibold mb-2">
                                Avançado
                              </h4>
                              <TextField
                                label="Posição da Lição (opcional)"
                                name="position"
                                type="number"
                                value={position}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setPosition(val === "" ? "" : Number(val));
                                }}
                                onBlur={() => {}}
                                disabled={loading.loading}
                                fullWidth
                                size="small"
                                inputProps={{ min: 0 }}
                                helperText="Defina a posição da lição no módulo (opcional)"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              </div>
              <div className="mt-4 gap-4 flex items-center justify-between border-t pt-4">
                <div className="flex-1">
                  {error && (
                    <Alert severity="error" className="text-sm">
                      {error}
                    </Alert>
                  )}
                </div>
                <div className="space-x-3">
                  <Button
                    disabled={loading.loading}
                    variant="outline"
                    type="reset"
                    onClick={closeModal}
                    className="min-w-[120px] py-3 h-fit"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    disabled={
                      loading.loading ||
                      !input.title ||
                      !input.description ||
                      !moduleId
                    }
                    type="submit"
                    className="min-w-[120px] py-3 h-fit"
                    size="sm"
                  >
                    Atualizar
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </Fade>
    </Modal>
  );
}
